import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import type { Message } from '../../../types/chat'
import { useNls } from '../../../nls/nlsProvider'
import { usePushBackHandler } from '../../../context/BackStackContext'
import { Pressable } from '../../Common/Pressable'

type Props = {
  message: Message | null
  isSelf: boolean
  canEdit: boolean
  canDelete: boolean
  translateAvailable: boolean
  onClose: () => void
  onReply: (message: Message) => void
  onCopy: (message: Message) => void
  onTranslate?: (message: Message) => void
  onSummarize?: (message: Message) => void
  onEdit?: (message: Message) => void
  onDelete?: (message: Message) => void
}

const summarize = (text: string, cap = 240): string => {
  const trimmed = (text || '').replace(/\s+/g, ' ').trim()
  return trimmed.length > cap ? `${trimmed.slice(0, cap - 1)}…` : trimmed
}

export const MemberChatContextSheet = ({
  message,
  isSelf,
  canEdit,
  canDelete,
  translateAvailable,
  onClose,
  onReply,
  onCopy,
  onTranslate,
  onSummarize,
  onEdit,
  onDelete,
}: Props) => {
  const { t } = useNls()
  usePushBackHandler(onClose, Boolean(message))

  useEffect(() => {
    if (!message) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [message, onClose])

  if (!message || typeof document === 'undefined') return null

  const items: Array<{ id: string; icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean }> = []

  items.push({
    id: 'reply',
    icon: <ReplyRoundedIcon fontSize="small" />,
    label: t('NLSChatActionReply'),
    onClick: () => { onReply(message); onClose() },
  })
  items.push({
    id: 'copy',
    icon: <ContentCopyRoundedIcon fontSize="small" />,
    label: t('NLSChatActionCopy'),
    onClick: () => { onCopy(message); onClose() },
  })
  if (translateAvailable && onTranslate && !isSelf) {
    items.push({
      id: 'translate',
      icon: <TranslateRoundedIcon fontSize="small" />,
      label: t('NLSChatActionTranslate'),
      onClick: () => { onTranslate(message); onClose() },
    })
  }
  if (onSummarize) {
    items.push({
      id: 'summarize',
      icon: <AutoAwesomeRoundedIcon fontSize="small" />,
      label: t('NLSChatActionSummarize'),
      onClick: () => { onSummarize(message); onClose() },
    })
  }
  if (canEdit && onEdit) {
    items.push({
      id: 'edit',
      icon: <EditRoundedIcon fontSize="small" />,
      label: t('NLSChatActionEdit'),
      onClick: () => { onEdit(message); onClose() },
    })
  }
  if (canDelete && onDelete) {
    items.push({
      id: 'delete',
      icon: <DeleteOutlineRoundedIcon fontSize="small" />,
      label: t('NLSChatActionDelete'),
      onClick: () => { onDelete(message); onClose() },
      danger: true,
    })
  }

  return createPortal(
    // eslint-disable-next-line no-restricted-syntax -- mobile chat long-press context action sheet uses custom sheet preview layout and is not a Common modal
    <div role="dialog"
      className="memberChatContext__root"
      aria-modal="true"
      aria-label={t('NLSChatActionsForMessage')}
      onClick={onClose}
    >
      <div
        className={`memberChatContext__preview ${isSelf ? 'isSelf' : ''}`}
        onClick={(event) => event.stopPropagation()}
      >
        {message.isDeleted ? t('NLSChatMessageDeleted') : summarize(message.textContent)}
      </div>
      <div
        className="memberChatContext__actions"
        onClick={(event) => event.stopPropagation()}
      >
        {items.map((item) => (
          <Pressable
            key={item.id}
            className={item.danger ? 'isDanger' : undefined}
            onClick={item.onClick}
          >
            {item.icon}
            {item.label}
          </Pressable>
        ))}
      </div>
    </div>,
    document.body,
  )
}
